import type { Diff } from '@manudev/model';
import { create } from 'zustand';
import { MODELS_BASE } from '@/hero/preload';
import { goToStation } from '@/lib/navigation';
import { useSelectionStore } from '@/state/selection';
import { hero, stations } from '@/state/tour';
import { saveBytes } from '@/store/download';
import { useHeroStore } from '@/store/store';
import { useAssistantStore } from '../build/state';

export type DiffKind = 'added' | 'removed' | 'changed';

export interface DiffRow {
  kind: DiffKind;
  globalId: string;
  type: string;
  name: string;
  fields: string[];
  // where the element is drawn, for focus and selection; null when it has no geometry
  target: {
    layer: string;
    expressID: number;
    center: [number, number, number];
    radius: number;
  } | null;
}

export type CompareStatus = 'idle' | 'diffing' | 'empty' | 'ready' | 'error';

interface CompareState {
  status: CompareStatus;
  diff: Diff | null;
  rows: DiffRow[];
  error: string | null;
  // 0 puts the cut at the far left (all edited), 1 at the far right (all original)
  swipe: number;
  setSwipe: (swipe: number) => void;
  setRows: (rows: DiffRow[]) => void;
  refresh: () => Promise<void>;
  buildFirst: () => void;
  download: () => void;
}

export const BUILD_QUESTION = 'put another floor on top';

// the newIds snapshot the current diff was computed from, so a revisit does not diff again
let diffed: number[] | null = null;
let seq = 0;
let awaiting = false;

export const useCompareStore = create<CompareState>((set, get) => ({
  status: 'idle',
  diff: null,
  rows: [],
  error: null,
  swipe: 0.5,
  setSwipe: (swipe) => set({ swipe }),
  setRows: (rows) => set({ rows }),
  refresh: async () => {
    const store = useHeroStore.getState();
    if (store.status !== 'ready') {
      set({ status: 'empty', diff: null, error: null });
      return;
    }
    if (diffed === store.newIds && get().status !== 'error') return;
    const token = ++seq;
    set({ status: 'diffing', error: null });
    try {
      const diff = await store.diff();
      if (token !== seq) return;
      diffed = store.newIds;
      const empty = !diff.added.length && !diff.removed.length && !diff.changed.length;
      set({ diff, status: empty ? 'empty' : 'ready' });
    } catch (err) {
      if (token !== seq) return;
      set({ status: 'error', error: (err as Error)?.message ?? String(err) });
    }
  },
  // Same path as typing the question in the build station, once the store can take edits.
  buildFirst: () => {
    const index = stations.findIndex((s) => s.kind === 'build');
    if (index < 0) return;
    awaiting = true;
    if (hero.ifc) useHeroStore.getState().load(MODELS_BASE + hero.ifc);
    whenStoreSettled(() => useAssistantStore.getState().ask(BUILD_QUESTION));
    goToStation(index);
  },
  download: () => {
    const { rows } = get();
    const pick = (kind: DiffKind) =>
      rows
        .filter((r) => r.kind === kind)
        .map(({ globalId, type, name, fields }) =>
          kind === 'changed' ? { globalId, type, name, fields } : { globalId, type, name },
        );
    const json = {
      model: hero.ifc ?? hero.name,
      added: pick('added'),
      removed: pick('removed'),
      changed: pick('changed'),
    };
    const bytes = new TextEncoder().encode(JSON.stringify(json, null, 2));
    saveBytes(bytes, `${hero.propsLayer}-diff.json`, 'application/json');
  },
}));

// The build the button asked for has answered: diff now, so the station is ready on return.
useSelectionStore.subscribe((s, prev) => {
  if (!awaiting || s.effects === prev.effects) return;
  if (!s.effects.some((e) => e.kind === 'build')) return;
  awaiting = false;
  void useCompareStore.getState().refresh();
});

function whenStoreSettled(run: () => void): void {
  const settled = () => {
    const { status } = useHeroStore.getState();
    return !hero.ifc || status === 'ready' || status === 'error';
  };
  if (settled()) {
    run();
    return;
  }
  const off = useHeroStore.subscribe(() => {
    if (!settled()) return;
    off();
    run();
  });
}
